import type { BusinessSegmentConditions, SegmentConditionGroups } from '@reach/shared-types/conditions';
import type {
  AvailableFieldsResponse,
  AvailableFieldsResponseWithTypes,
} from '../engage-segments-tab/segment-definitions-api';
import styles from './EditSegment.module.css';
import { RecipientsPreview } from './RecipientsPreview';
import { SegmentDefinitionEditor } from './SegmentDefinitionEditor';
import { EditorModeToggle } from './visual-builder/EditorModeToggle';
import { SegmentVisualBuilder } from './visual-builder/SegmentVisualBuilder';

export interface SegmentDefinitionSectionProps {
  formatType: 'legacy' | 'new';
  editorMode: 'visual' | 'json';
  onEditorModeChange: (mode: 'visual' | 'json') => void;
  contentsJson: string;
  onContentsChange: (json: string) => void;
  onValidationChange: (valid: boolean) => void;
  isJsonValid: boolean;
  isSaving: boolean;
  availableFields: AvailableFieldsResponse;
  isLoadingFields: boolean;
  fieldsError: Error | null;
  availableFieldsWithTypes: AvailableFieldsResponseWithTypes | undefined;
  isLoadingFieldsWithTypes: boolean;
  parsedConditions: BusinessSegmentConditions | null;
  parsedConditionGroups: SegmentConditionGroups | null;
  conditionGroupsForPreview: SegmentConditionGroups | null;
}

export function SegmentDefinitionSection({
  formatType,
  editorMode,
  onEditorModeChange,
  contentsJson,
  onContentsChange,
  onValidationChange,
  isJsonValid,
  isSaving,
  availableFields,
  isLoadingFields,
  fieldsError,
  availableFieldsWithTypes,
  isLoadingFieldsWithTypes,
  parsedConditions,
  parsedConditionGroups,
  conditionGroupsForPreview,
}: SegmentDefinitionSectionProps) {
  return (
    <section className={styles.columns}>
      <article className={styles.editor} id="segment-editor-panel">
        <header>
          <h2>Segment Definition</h2>
          <p>
            {formatType === 'legacy'
              ? 'Define your segment using JSON conditions'
              : editorMode === 'visual'
                ? 'Build your segment with the visual builder'
                : 'Define your segment using JSON condition groups'}
          </p>
          {formatType === 'new' && (
            <EditorModeToggle mode={editorMode} onChange={onEditorModeChange} disabled={isSaving} />
          )}
        </header>
        {formatType === 'legacy' ? (
          <SegmentDefinitionEditor
            contentsJson={contentsJson}
            onContentsChange={onContentsChange}
            onValidationChange={onValidationChange}
            availableFields={availableFields}
            isLoadingFields={isLoadingFields}
            fieldsError={fieldsError}
            isReadOnly={isSaving}
            useLegacyFieldNames
            hideHeader
          />
        ) : editorMode === 'visual' ? (
          availableFieldsWithTypes ? (
            <SegmentVisualBuilder
              key="visual"
              conditionGroups={parsedConditionGroups ?? []}
              onChange={groups => onContentsChange(JSON.stringify(groups, null, 2))}
              availableFields={availableFieldsWithTypes}
              isLoading={isLoadingFieldsWithTypes}
            />
          ) : (
            <p className={styles.loadingState}>Loading fields...</p>
          )
        ) : (
          <SegmentDefinitionEditor
            contentsJson={contentsJson}
            onContentsChange={onContentsChange}
            onValidationChange={onValidationChange}
            availableFields={availableFields}
            isLoadingFields={isLoadingFields}
            fieldsError={fieldsError}
            isReadOnly={isSaving}
            useLegacyFieldNames={false}
            hideHeader
          />
        )}
      </article>

      <RecipientsPreview
        conditions={formatType === 'legacy' ? parsedConditions : null}
        conditionGroups={formatType === 'new' ? conditionGroupsForPreview : null}
        isJsonValid={isJsonValid}
      />
    </section>
  );
}
